import React from "react";
import { Braces, Check, Filter, ShieldAlert, X } from "lucide-react";
import { Badge, SectionHeader } from "./Primitives";
import { useLocale } from "../lib/i18n";
import { criterionDescription } from "../lib/bilingualSidecar";

const CriterionRow = ({ item }) => {
  const { locale, t } = useLocale();
  const metadata = item.metadata || {};
  const kindLabel = item.kind === "judge"
    ? t("rubric.kindJudge")
    : item.kind === "gate"
      ? t("rubric.kindGate")
      : t("rubric.kindProgrammatic");
  return (
    <article className={`criterion-card criterion-${item.kind}`}>
      <div className="criterion-id">
        <code>{item.id || "—"}</code>
        <Badge tone={item.kind === "gate" ? "red" : item.kind === "programmatic" ? "blue" : "neutral"}>
          {kindLabel}
        </Badge>
      </div>
      <p>{criterionDescription(item, locale) || t("rubric.decoratorDescription")}</p>
      {item.kind !== "programmatic" ? (
        <small className={item.translation ? "translation-bound" : "translation-stale"}>
          {item.translation
            ? t("rubric.translationBound", { count: 1 })
            : t("rubric.translationStale")}
        </small>
      ) : null}
      <div className="criterion-meta">
        {item.weight !== undefined ? <span>w {item.weight}</span> : <span>{t("rubric.veto")}</span>}
        {item.negate ? <span className="negative">{t("rubric.negative")}</span> : <span>{t("rubric.positive")}</span>}
        {metadata.dimension ? <span>{metadata.dimension}</span> : null}
        {metadata.criterion_necessity ? <span>{metadata.criterion_necessity}</span> : null}
      </div>
    </article>
  );
};

export default function RubricView({ sample }) {
  const { t } = useLocale();
  const [filter, setFilter] = React.useState("all");
  const criteria = sample.analysis.criteria.filter((item) => filter === "all" || item.kind === filter);
  return (
    <div className="page-stack">
      <section>
        <SectionHeader
          eyebrow={t("rubric.eyebrow")}
          title={t("rubric.title")}
          aside={
            <div className="segmented">
              {["all", "judge", "gate", "programmatic"].map((kind) => (
                <button key={kind} className={filter === kind ? "active" : ""} onClick={() => setFilter(kind)}>
                  {t(`rubric.${kind}`)}
                </button>
              ))}
            </div>
          }
        />
        <div className="rubric-summary">
          <div><strong>{sample.analysis.metrics.judgeCount}</strong><span>{t("rubric.llmJudge")}</span></div>
          <div><strong>{sample.analysis.metrics.gateCount}</strong><span>{t("rubric.hardGates")}</span></div>
          <div><strong>{sample.analysis.metrics.programmaticCount}</strong><span>{t("rubric.programmaticCount")}</span></div>
          <div><strong>{sample.analysis.metrics.criticalCount}</strong><span>{t("rubric.weight10")}</span></div>
          <div><strong>{(sample.analysis.metrics.programmaticShare * 100).toFixed(1)}%</strong><span>{t("rubric.programmaticWeight")}</span></div>
        </div>
        <div className="criteria-list">
          {criteria.map((item, index) => <CriterionRow item={item} key={`${item.kind}-${item.id}-${index}`} />)}
        </div>
      </section>

      <section>
        <SectionHeader eyebrow={t("rubric.coverageEyebrow")} title={t("rubric.coverageTitle")} />
        <div className="coverage-grid">
          {sample.coverage.map((item, index) => (
            <div className={`coverage-row ${item.anchored ? "coverage-pass" : "coverage-warn"}`} key={`${item.id}-${index}`}>
              {item.anchored ? <Check size={14} /> : <X size={14} />}
              <code>{item.id}</code>
              <span>{item.anchorTokens.length ? item.anchorTokens.join(" · ") : t("rubric.noAnchor")}</span>
            </div>
          ))}
        </div>
        <p className="fine-print">{t("rubric.coverageNote")}</p>
        <p className="fine-print">{t("rubric.translationPending")}</p>
      </section>
    </div>
  );
}
